<template>
  <div v-show="isLoadingInProgress" class="flex items-center justify-center">
    <Loader color="#64748B" width="50px" height="50px" />
  </div>
  <div v-show="!isLoadingInProgress">
    <div class="transaction-explorer mt-14 mb-14">
      <div class="mb-[10px]">
        <Paragraph class="text-blue-dark font-jost-regular text-[25px]" text="Counterparty ID" />
        <div class="flex gap-[20px] max-w-max relative w-auto">
          <p class="font-jost-regular text-blue-light text-[33px] leading-[36px] break-all w-full text-left">
            {{ counterpartyId }}
            <span class="inline-block cursor-pointer" title="Copy hash">
              <CopyIcon :isActive="checkIfIsEqual(counterpartyId)" @click="copy(counterpartyId)" title="Copy hash"
                class="h-[33px] ml-2 inline-block cursor-pointer" />
            </span>
          </p>
        </div>
        <h1 v-if="searchData && searchData" class="text-blue-dark text-[40px] font-jost-bold mt-[10px]">
          {{ getStackTitle(searchData) }}
        </h1>
      </div>

      <section class="t-results mt-[86px] grid grid-cols-2">
        <div class="nfts__wrapper flex gap-[45px]">
          <div v-if="searchData && searchData.nfts" class="num-subgroups flex flex-col gap-[10px]">
            <p @click="scrollToNft(nft.txRefHash)" v-for="(nft, index) in searchData.nfts" :key="index"
              class="font-jost-black cursor-pointer flex items-center justify-center w-[34px] h-[34px] bg-[#D9D9D9] rounded-[53px] text-white"
              :class="{'is-active': checkIfIsActive(index)}">{{ index+1 }}</p>
          </div>

          <div class="scrollable" ref="scrollable">
            <template v-if="searchData && searchData.nfts">
              <template v-for="(nft) in searchData.nfts" :key="nft.id">
                <div v-if="!isTransfer(nft)" :id="nft.txRefHash"
                  class="subgroup-item scroll-item bg-white p-[28px] rounded-[8px] max-w-[565px] break-all w-full text-[18px] text-blue-dark leading-[21px] mb-[60px]">
                  <div v-if="getNftTitle(nft)"
                    class="flex gap-[10px] text-blue-dark font-inter-medium text-[20px] leading-[26px] mb-[16px]">
                    <HeadingComponent
                      class="text-blue-dark inline-block font-inter-medium text-[20px] leading-[26px] break-words"
                      :text="`Title: ${getNftTitle(nft)}`" />
                  </div>

                  <NftImage :file-url="getNftFileUrl(nft)" />
                  <div class="mt-[16px] text-inter-regular">
                    <p v-if="getCreatorName(nft)">
                      Creator: {{ getCreatorName(nft) }}
                    </p><br />
                    <p v-if="getPrompt(nft)" class="!text-inter-medium break-all max-w-full">
                      Description: {{ getPrompt(nft).length >= 60 ? `${getPrompt(nft)}...` : getPrompt(nft) }}
                    </p><br />
                    <p v-if="getHashtags(nft)" class="!text-inter-medium break-all max-w-full">
                      Hashtags: {{ getHashtags(nft) }}
                    </p><br />
                  </div>
                  <div class="flex justify-between gap-[10px] flex-wrap">
                    <p class="text-[#898989]">Signed by
                      <router-link v-if="searchData && searchData.owner"
                        :to="{ name: 'account', params: { chainNumber: searchData.chainNumber || searchData.chainId || $route.params.chainNumber || $route.params.chain, address: searchData.owner } }"
                        class="text-blue-light !text-inter-medium">
                        Recipient_CAA
                      </router-link>
                    </p>
                    <p class="text-[#898989]">Appended at
                      <router-link v-if="nft && nft.txRefHash"
                        :to="{ name: 'transaction', params: { chainNumber: nft.chainNumber || searchData.chainNumber || searchData.chainId || $route.params.chainNumber || $route.params.chain, txRefhash: nft.txRefHash } }"
                        class="text-blue-light !text-inter-medium">
                        Tx_RefHash
                      </router-link>
                    </p>
                  </div>
                </div>
                <div v-else :id="nft.txRefHash"
                  class="subgroup-item flex flex-col justify-between scroll-item bg-white p-[28px] rounded-[8px] max-w-[565px] break-all w-full text-[18px] text-blue-dark leading-[21px] mb-[60px]">
                  <div>
                    <div class="flex gap-[10px] text-blue-dark font-inter-medium text-[20px] leading-[26px] mb-[16px]">
                      <HeadingComponent
                        class="text-blue-dark inline-block font-inter-medium text-[20px] leading-[26px] break-words"
                        text="Stack transfer" />
                    </div>
                    <div class="mt-[20px]">
                      <p v-if="getNftMessage(nft)" class="!text-inter-medium break-all max-w-full">
                        Message: {{ getNftMessage(nft).length >= 200 ? `${getNftMessage(nft)}...` : getNftMessage(nft)
                        }}
                      </p>
                    </div>
                  </div>
                  <div>
                    <div class="col font-inter-regular text-blue-dark block text-[18px] leading-[21px] flex relative">
                      Creator
                    </div>
                    <div class="overview__hashes max-w-[450px] flex items-center gap-[20px]">
                      <router-link v-if="getTransferLayerSender(nft)"
                        :to="{ name: 'account', params: { chainNumber: nft.chainNumber || searchData.chainNumber || searchData.chainId || $route.params.chainNumber || $route.params.chain, address: getTransferLayerSender(nft) } }"
                        class="text-blue-dark !text-inter-medium !block flex relative text-blue-dark cursor-pointer text-ellipsis">
                        {{ isMobile ? getShortenedText(getTransferLayerSender(nft)) : getTransferLayerSender(nft) }}
                      </router-link>
                      <CopyIcon :isActive="checkIfIsEqual(getTransferLayerSender(nft), 'creator')"
                        @click="copy(getTransferLayerSender(nft), 'creator')" title="Copy hash"
                        class="h-[33px] ml-2 inline-block cursor-pointer" />
                    </div>
                    <br />
                    <div class="col font-inter-regular text-blue-dark block text-[18px] leading-[21px] flex relative">
                      Owner
                    </div>
                    <div class="overview__hashes max-w-[450px] flex items-center gap-[20px]">
                      <router-link v-if="getTransferLayerReceiver(nft)"
                        :to="{ name: 'account', params: { chainNumber: nft.chainNumber || searchData.chainNumber || searchData.chainId || $route.params.chainNumber || $route.params.chain, address: getTransferLayerReceiver(nft) } }"
                        class="text-blue-dark !text-inter-medium !block flex relative text-blue-dark cursor-pointer text-ellipsis">
                        {{ isMobile ? getShortenedText(getTransferLayerReceiver(nft)) : getTransferLayerReceiver(nft) }}
                      </router-link>
                      <CopyIcon :isActive="checkIfIsEqual(getTransferLayerReceiver(nft), 'owner')"
                        @click="copy(getTransferLayerReceiver(nft), 'owner')" title="Copy hash"
                        class="h-[33px] ml-2 inline-block cursor-pointer" />
                    </div>
                  </div>


                  <div class="flex gap-[63px]">
                    <p class="text-[#898989]">Signed by
                      <router-link v-if="searchData && searchData.owner"
                        :to="{ name: 'account', params: { chainNumber: searchData.chainNumber || searchData.chainId || $route.params.chainNumber || $route.params.chain, address: searchData.owner } }"
                        class="text-blue-light !text-inter-medium">
                        Recipient_CAA
                      </router-link>
                    </p>
                    <p class="text-[#898989]">Appended at
                      <router-link v-if="nft && nft.txRefHash"
                        :to="{ name: 'transaction', params: { chainNumber: nft.chainNumber || searchData.chainNumber || searchData.chainId || $route.params.chainNumber || $route.params.chain, txRefhash: nft.txRefHash } }"
                        class="text-blue-light !text-inter-medium">
                        Tx_RefHash
                      </router-link>
                    </p>
                  </div>
                </div>
              </template>
            </template>
          </div>
        </div>

        <section class="t-results-body" v-if="searchData && searchData.nfts">
          <Paragraph class="font-inter-regular text-blue-dark text-[25px] leading-[30px] mb-[10px]" text="Overview" />
          <div class="bg-white px-[21px] py-[28px] rounded-[8px]">
            <div
              class="row text-left items-center text-blue-dark px-2.5 py-3.5 result-row border-b border-blue-light flex items-baseline">
              <div class="col font-inter-regular text-blue-dark block text-[18px] leading-[21px] flex relative">
                Owner
              </div>
              <div class="overview__hashes max-w-[450px] flex gap-[20px]">
                <router-link v-if="searchData && searchData.owner"
                  :to="{name: 'account', params: { chainNumber: searchData.chainNumber || searchData.chainId || $route.params.chainNumber || $route.params.chain, address: searchData.owner}}"
                  class="text-blue-dark !text-inter-medium !block flex relative text-blue-dark cursor-pointer text-ellipsis">
                  {{ isMobile? getShortenedText(searchData.owner): searchData.owner }}
                </router-link>
                <CopyIcon :isActive="checkIfIsEqual(searchData.owner, 'owner')" @click="copy(searchData.owner, 'owner')"
                  title="Copy hash" class="h-[33px] ml-2 inline-block cursor-pointer" />
              </div>

            </div>
            <div
              class="row text-left items-center text-blue-dark px-2.5 py-3.5 result-row border-b border-blue-light flex items-baseline">
              <div class="col font-inter-regular text-blue-dark block text-[18px] leading-[21px] flex relative">
                Creator
              </div>
              <div class="overview__hashes max-w-[450px] flex gap-[20px]">
                <router-link v-if="searchData && searchData.creator"
                  :to="{name: 'account', params: { chainNumber: searchData.chainNumber || searchData.chainId || $route.params.chainNumber || $route.params.chain, address: searchData.creator}}"
                  class="text-blue-dark !text-inter-medium !block flex relative text-blue-dark cursor-pointer text-ellipsis">
                  {{ isMobile? getShortenedText(searchData.creator): searchData.creator }}
                </router-link>
                <CopyIcon :isActive="checkIfIsEqual(searchData.creator, 'creator')"
                  @click="copy(searchData.creator, 'creator')" title="Copy hash"
                  class="h-[33px] ml-2 inline-block cursor-pointer" />
              </div>
            </div>
            <div v-if="searchData && searchData.chainId"
              class="row text-left items-center text-blue-dark px-2.5 py-3.5 result-row border-b border-blue-light flex items-baseline">
              <div class="col font-inter-regular text-blue-dark block text-[18px] leading-[21px] flex relative">
                Chain
              </div>
              <div class="col font-jost-regular px-2.5 text-blue-dark">
                <span class="block text-ellipsis text-[16px]">
                  #{{ searchData.chainId }}
                </span>
              </div>
            </div>

            <div
              class="row text-left items-center text-blue-dark px-2.5 py-3.5 result-row border-b border-blue-light flex items-baseline">
              <div class="col font-inter-regular text-blue-dark block text-[18px] leading-[21px] flex relative">
                Stack Length
              </div>
              <div class="col font-jost-regular px-2.5 text-blue-dark">
                <span class="block text-ellipsis text-[16px]">
                  {{ searchData && searchData.nfts ? searchData.nfts.length : '' }}
                </span>
              </div>
            </div>

            <div class="row text-left items-center text-blue-dark px-2.5 py-3.5 result-row flex items-baseline">
              <div class="col font-inter-regular text-blue-dark block text-[18px] leading-[21px] flex relative">
                First Unit At
              </div>
              <div class="!w-[70%] flex justify-between  px-2.5 text-ellipsis text-blue-dark">
                <div>
                  <span class="block font-jost-regular text-ellipsis">
                    Block #
                    <router-link v-if="searchData && searchData.blockNumber"
                      :to="{name: 'block', params: {chainNumber: searchData.chainNumber || $route.params.chainNumber || $route.params.chain, blockNumber: searchData.blockNumber}}" class="text-blue-dark">
                      {{ searchData.blockNumber }}
                    </router-link>
                  </span>
                  <span class="block font-jost-regular text-ellipsis">
                    {{ stackCreatedAt }}
                  </span>
                </div>
                <router-link v-if="searchData && searchData.nfts"
                  :to="{name: 'transaction', params: { chainNumber: searchData.chainNumber || searchData.chainId || $route.params.chainNumber || $route.params.chain, txRefhash: searchData.nfts[0].txRefHash}}"
                  class="text-blue-light !text-inter-medium">
                  <img class="cursor-pointer" src="/images/icons/icon-ionic-md-open.svg" />
                </router-link>
              </div>
            </div>
          </div>
        </section>

      </section>
      <div v-if="stackTransactions && stackTransactions.length" class="mb-7 mt-[86px]">
        <StackTransactionsTbl :transactions="stackTransactions" :stack="searchData" :copied="copied"
          @copied="setCopied" />
        <div class="flex justify-end px-[25px]">
          <pagination-component :items="stackTxsDetails.totalCount" :initialPage="getInitialPage()"
            @changePage="setFoundResult" class="float-left" />
        </div>
      </div>
      <Paragraph text="We couldn't find any results.." class="text-blue-dark text-lg mt-5 float-left" v-else />
    </div>
  </div>
</template>

<script>
import "vue-json-pretty/lib/styles.css";
import Paragraph from "@/components/web/ParagraphComponent";
import { mapGetters } from 'vuex';
import api from '@/helpers/api';
import string from '@/helpers/string';
import CopyIcon from '@/components/svg/CopyIcon';
import HeadingComponent from '@/components/web/HeadingComponent';
import StackTransactionsTbl from "@/components/common/StackTransactionsTbl.vue";
import PaginationComponent from "@/components/common/PaginationComponent";
import NftImage from "@/components/common/NftImage";
import Loader from '@/components/common/Loader.vue';

import { maxTableRows } from "@/utils/pagination";
import { isMobileScreen } from "@/helpers/common";

import moment from 'moment';

export default {
  name: "CounterpartyComponent",
  components: {
    Paragraph,
    CopyIcon,
    HeadingComponent,
    StackTransactionsTbl,
    PaginationComponent,
    NftImage,
    Loader,
  },
  watch: {
    'searchData.nfts': {
      handler: function (nfts) {
        if(nfts) {
          this.currentShowingNftHash = this.$route.query.hash;

          if (this.currentShowingNftHash) {
            setTimeout(() => {
              this.scrollToNft(this.currentShowingNftHash, true);
            }, 1000);
          }
        }
      },
      immediate: true
    }
  },
  data: () => {
    return {
      searchData: null,
      isLoadingInProgress: false,
      copied: '',
      isMobile: isMobileScreen(),
      initialPage: 1,
      pageSize: maxTableRows,
      transactionRefHash: "",
      currentShowingNftHash: "",
      isLoading: false,
      virtualFlag: false,   // vue-json-pretty setting
      showLine: true,
      lastPath: null,
      collapsedOnClickBrackets: true,
      stackTxsDetails: null,
      currentScrollItem: 1,
      transactionData: {},
      uatOrderedSample: {
        "uatData": null,
        "signature": null,
      },
      uatDataOrderedSample: {
        "groupType": null,
        "blockNumber": null,
        "chainNumber": null,
        "userPublicKey": null,
        "payloadRefHash": null,
      },
      isScrolling: false,
    }
  },
  computed: {
    counterpartyId() {
      return this.$route.params.counterpartyId || '';
    },
    stackTransactions() {
      return this.stackTxsDetails && this.stackTxsDetails.items && this.stackTxsDetails.items.length ? this.stackTxsDetails.items : [];
    },
    routeName() {
      return this.$route.name
    },
    stackCreatedAt() {
      if (!this.searchData || (this.searchData && !this.searchData.createdAt)) return;
      const date = moment(this.searchData.createdAt);

      return date.format('YYYY-MM-DD H:mm:ss');
    }
  },
  created() {
    this.lastPath = this.$router.options.history.state.back;

    this.searchByCounterpartyId(this.counterpartyId);
    this.getTransactionsByCounterpartyId(this.counterpartyId, maxTableRows, 0);
  },
  mounted() {
    const scrollContainer = this.$refs.scrollable;

    if (!scrollContainer) return;
    scrollContainer.addEventListener('scroll', this.handleScroll);
  },
  methods: {
    checkIfIsActive(index) {
      return this.currentScrollItem === index + 1;
    },
    scrollToNft(hash, fromMounted) {
      if (!hash) return;
      const parentDiv = this.$refs.scrollable;
      const childElement = document.getElementById(hash);

      if (childElement && parentDiv) {
        const parentRect = parentDiv.getBoundingClientRect();
        const childRect = childElement.getBoundingClientRect();

        const offset = childRect.top - parentRect.top + parentDiv.scrollTop;

        parentDiv.scrollTo({ top: offset, behavior: 'smooth' });

        if (fromMounted) {
          setTimeout(() => {
            this.currentScrollItem = Array.from(parentDiv.querySelectorAll('.scroll-item')).findIndex(item => item.id === this.currentShowingNftHash) + 1;
          }, 1500);
        }
      }
    },
    getShortenedText(str) {
      return string.getShortenHash(str)
    },
    getItem(page) {
      const normalizedPage = Number(page) > 0 ? Number(page) : 1;
      const skip = (normalizedPage - 1) * maxTableRows;
      this.getTransactionsByCounterpartyId(this.counterpartyId, maxTableRows, skip)
    },
    setFoundResult(data) {
      const page = Number(data?.page) > 0 ? Number(data.page) : 1
      this.getItem(page)
    },
    getInitialPage() {
      let page = this.initialPage;
      if (Object.keys(this.stackTxsDetails.items).length) {
        page = this.stackTxsDetails.meta ? this.stackTxsDetails.meta.skip / this.pageSize + 1 : this.initialPage;
      }
      return page;
    },
    getStackTitle(stack) {
      if (!stack || !stack.nfts || !stack.nfts.length) return '';

      const title = this.findItemStartingWith(stack.nfts[0].metas, 's:');
      return title ? title : '';
    },
    getNftTitle(nft) {
      const title = this.findItemStartingWith(nft.metas, 'l:');
      return title ? title : '';
    },
    getCreatorName(nft) {
      const artistName = this.findItemStartingWith(nft.metas, 'u:');
      return artistName ? artistName : '';
    },
    getTransferLayerSender(nft) {
      const sender = this.findItemStartingWith(nft.metas, 's:');
      return sender ? sender : '';
    },
    getTransferLayerReceiver(nft) {
      const receiver = this.findItemStartingWith(nft.metas, 'r:');
      return receiver ? receiver : '';
    },
    getNftFileUrl(nft) {
      const fileUrl = this.findItemStartingWith(nft.metas, 'i:');
      return fileUrl ? fileUrl : '';
    },
    isTransfer(nft) {
      const transferMeta = this.findItemStartingWith(nft.metas, 't:');
      return transferMeta ? transferMeta : '';
    },
    getPrompt(nft) {
      const prompt = this.findItemStartingWith(nft.metas, 'p:');
      return prompt ? prompt : '';
    },
    getHashtags(nft) {
      const tags = this.findItemStartingWith(nft.metas, 'h:');
      return tags ? tags : '';
    },
    getNftMessage(nft) {
      const msg = this.findItemStartingWith(nft.metas, 'm:');
      return msg ? msg : '';
    },
    findItemStartingWith(arr, keyName) {
      if (!arr || !arr.length) return null;

      for (const item of arr) {
        if (item.startsWith(keyName)) {
          return item.substring(2);
        }
      }
      return null;
    },
    handleScroll(e) {
      const scrollContainer = e.target;
      const scrollItems = Array.from(scrollContainer.querySelectorAll('.scroll-item'));
      const currentScrollItem = scrollItems.findIndex((item, index) => {
        return item.getBoundingClientRect().top + 150 > 0;
      });
      this.currentScrollItem = currentScrollItem + 1;
    },
    getLayerTitle(layer) {
      return this.routeName === "transaction" ? "AppCommitHash" : "RefHash"
    },
    getTitle() {
      return this.routeName === "transaction" ? "AppCommitHash" : "RefHash"
    },
    getHash() {
      return this.searchData.id;
    },
    goBack() {
      this.$router.push({name: 'search-result'})
    },
    setupTransactionData() {
      if (!this.transactionData || !this.transactionData.uat) return;
      this.transactionRefHash = this.txRefHash || this.transactionData.uat.uatData.refHash;

      this.transactionData.uat = Object.assign(this.uatOrderedSample, this.transactionData.uat);

      for (const key in this.uatDataOrderedSample) {
        this.uatDataOrderedSample[key] = this.transactionData.uat.uatData[key];
        if (key === 'userPublicKey') {
          this.uatDataOrderedSample[key] = this.transactionData.uat.uatData.pubKeyUser;
        }
      }
      this.transactionData.uat.uatData = Object.assign(this.uatDataOrderedSample)
    },
    getTransactionsByCounterpartyId(counterpartyId, maxTableRows, page) {
      return api.getStackTransactionsById(counterpartyId, maxTableRows, page)
        .then((res) => {
          const data = res.data;
          this.stackTxsDetails = data;
      })
      .catch((err) => {
        this.isLoadingInProgress = false;
      })
    },
    searchByCounterpartyId(counterpartyId) {
      this.isLoadingInProgress = true;

      return api.getTransactionByCounterpartyId(counterpartyId)
        .then((res) => {
          const data = res.data;
          this.searchData = data;
          this.isLoadingInProgress = false;
      })
      .catch((err) => {
          this.isLoadingInProgress = false;
      })
    },
    searchByTxRefHash(txRefHash) {
      this.$store.commit('setIsLoadingInProgress', true);
      return api.getTransactionDetailsByRefHash(txRefHash)
        .then((res) => {
          this.transactionData = res.data
          this.setupTransactionData()
          this.$store.commit('setIsLoadingInProgress', false);
        })
        .catch((err) => {
          // this.$toast.error('Something went wrong..', {
          //   position: 'bottom'
          // })
          this.$store.commit('setIsLoadingInProgress', false);
        })
    },
    copy(hash, uId = 0) {
      string.copyToClipboard(hash);
      this.setCopied(hash + uId);
    },
    checkIfIsEqual(str, uId = 0) {
      return string.isTextEqualTo(str, this.copied, uId);
    },
    setCopied(hash) {
      this.copied = hash;
    },
  }
}
</script>
<style>
.vjs-value, .vjs-value-string {
  color: #2699FB !important;
  font-size: 18px;
  line-height: 26px;
  font-family: 'Inter-Regular', monospace !important;
}
.transaction-explorer .vjs-value {
  color: #2699FB !important;
  font-size: 18px;
  line-height: 26px;
  font-family: 'Inter-Regular', monospace !important;
}
.transaction-explorer .vjs-tree {
  font-size: 18px;
  line-height: 26px;
  overflow-x: auto !important;
  white-space: nowrap;
}
.transaction-explorer .vjs-key {
  font-family: 'Inter-Regular', monospace !important;
}
</style>
<style scoped>
.col {
  width: 30%;
}
.t-results-body {
  padding: 0 15px 10px 15px;
  border-radius: 9px 9px 9px 9px;
}
.t-results-body .row:first-child {
  border-top: unset;
}
.is-active {
  background-color: #D66339;
}

.scrollable {
  height: fit-content;
  max-height: 690px;
  overflow-y: scroll;
  scrollbar-width: none; /* For Firefox */
  -ms-overflow-style: none;  /* For Internet Explorer and Edge */
  /* scroll-snap-type: y mandatory; */
}

.scrollable::-webkit-scrollbar {
  width: 0px; /* For Chrome, Safari, and Opera */
}

.scroll-item {
  min-height: 500px;
  scroll-snap-align: start;
  position: relative;
}

.scroll-item:not(:last-child)::after {
  content: "";
  position: absolute;
  left: 50%;
  bottom: -60px;
  height: 60px; /* Same as the scroll-item height */
  width: 2px;
  background: #0095C5; /* Adjust as needed */
  transform: translateX(-50%);
}
@media (max-width: 1400px) {
  .t-results {
    display: flex;
      flex-wrap: wrap;
      flex-direction: column-reverse;
      gap: 60px;
      align-items: initial;
  }
  .nfts__wrapper {
    margin: auto;
  }
}
@media (max-width: 725px) {
  .scroll-item .subgroup__image__container img {
    max-width: 100% !important;
  }
  .scroll-item {
    min-height: 400px;
  }
  .scrollable {
    max-height: 550px;
  }
  .nfts__wrapper {
    gap: 10px
  }
  .subgroup-item {
    max-width: 400px !important;
  }
  .subgroup__image__container {
    width: 100% !important;
    height: auto !important;
  }
  .scroll-item {
    height: fit-content !important;
  }
  .t-results-body {
    padding: 0 !important;
  }
  .overview__hashes {
    max-width: 320px !important;
  }
}

@media (max-width: 500px) {
  .nfts__wrapper {
    gap: 10px
  }
  .subgroup-item {
    max-width: 320px !important;
  }
  .subgroup__image__container {
    width: 100% !important;
    height: auto !important;
  }
  .scroll-item {
    height: fit-content !important;
  }
  .t-results-body {
    padding: 0 !important;
  }
  .overview__hashes {
    max-width: 280px !important;
  }
}
</style>
